import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { ToDo, ToDoCategory, ToDoStatus, ToDoWrite } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  ConflictBanner, EmptyRow, ErrorBanner, ExportMenu, MultiSelect, Select, SlideOver, SortableHeader, downloadCsv, formatDate, today, useTableSort,
} from '../components/ui';

const STATUSES: ToDoStatus[] = ['overdue', 'open', 'done'];

const STATUS_LABEL: Record<ToDoStatus, string> = {
  overdue: 'Overdue',
  open: 'Open',
  done: 'Done',
};

const CATEGORIES: ToDoCategory[] = ['vehicle', 'trailer', 'tractor', 'shop', 'general'];

/** What an item is about. A particular truck is named in the description. */
const CATEGORY_LABEL: Record<ToDoCategory, string> = {
  vehicle: 'Vehicle',
  trailer: 'Trailer',
  tractor: 'Tractor',
  shop: 'Shop / vendor',
  general: 'General',
};

/** What a to-do saves as, with only the tick changed. */
const writeOf = (t: ToDo, done: boolean): ToDoWrite => ({
  enteredOn: t.enteredOn,
  description: t.description,
  dueOn: t.dueOn ?? null,
  category: t.category,
  done,
  doneOn: done ? t.doneOn ?? null : null,
  resolution: t.resolution ?? null,
  rowVersionNo: t.rowVersionNo,
});

/**
 * Fleet's to-do list: action items with the day they were entered, when
 * they're expected done, and a tick once they are. Opens on what is still
 * open, the overdue ones first.
 */
export function ToDos() {
  const { can } = useApp();
  const canKeep = can('vehicles', 'todo');
  const queryClient = useQueryClient();
  const todos = useQuery({ queryKey: ['todos'], queryFn: () => api.get<ToDo[]>('/api/todos') });
  const [statuses, setStatuses] = useState<string[]>(['overdue', 'open']);
  const [categories, setCategories] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ToDo | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const all = useMemo(() => todos.data ?? [], [todos.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((t) =>
      (statuses.length === 0 || statuses.includes(t.status))
      && (categories.length === 0 || categories.includes(t.category))
      && (!q || [t.description, t.resolution, t.createdByName, t.doneByName].filter(Boolean).join(' ').toLowerCase().includes(q)));
  }, [all, statuses, categories, search]);

  const { rows: sorted, sort, onSort } = useTableSort(rows, {
    done: (t) => STATUSES.indexOf(t.status),
    enteredOn: (t) => t.enteredOn,
    description: (t) => t.description.toLowerCase(),
    category: (t) => CATEGORIES.indexOf(t.category),
    dueOn: (t) => t.dueOn,
    doneOn: (t) => t.doneOn,
  });

  // The tick in the list: done today by whoever ticks it, or open again.
  const tick = useMutation({
    mutationFn: ({ t, done }: { t: ToDo; done: boolean }) => api.put(`/api/todos/${t.toDoId}`, writeOf(t, done)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['todos'] }),
    onError: (e: Error) => setError(e.message),
  });

  const count = (s: ToDoStatus) => all.filter((t) => t.status === s).length;
  const dueThisWeek = all.filter((t) => t.status === 'open' && t.dueOn && t.dueOn <= addDays(today(), 7)).length;

  const exportRows = () => downloadCsv(
    `fleet-to-do-${today()}.csv`,
    ['Entered', 'Description', 'Type', 'Expected done', 'Status', 'Done on', 'Done by', 'Outcome', 'Entered by'],
    sorted.map((t) => [
      t.enteredOn, t.description, CATEGORY_LABEL[t.category], t.dueOn, STATUS_LABEL[t.status], t.doneOn, t.doneByName, t.resolution, t.createdByName,
    ]),
  );

  const onTick = (t: ToDo, done: boolean) => { setError(null); tick.mutate({ t, done }); };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">To-do</div>
          <div className="page-subtitle">Action items for the fleet, and when each is expected done</div>
        </div>
      </div>

      {todos.isError && <ErrorBanner message={(todos.error as Error).message} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {todos.data && (
        <div className="stat-grid">
          <div className="stat stat--clickable" onClick={() => setStatuses(['overdue', 'open'])}>
            <div className="stat__label">OPEN</div>
            <div className="stat__value">{count('open') + count('overdue')}</div>
            <div className="stat__note">not done yet</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStatuses(['overdue'])}>
            <div className="stat__label">OVERDUE</div>
            <div className="stat__value" style={{ color: count('overdue') ? 'var(--red)' : undefined }}>{count('overdue')}</div>
            <div className="stat__note">past the date expected</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStatuses(['open'])}>
            <div className="stat__label">DUE IN 7 DAYS</div>
            <div className="stat__value" style={{ color: dueThisWeek ? 'var(--orange-text)' : undefined }}>{dueThisWeek}</div>
            <div className="stat__note">expected by {formatDate(addDays(today(), 7))}</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStatuses(['done'])}>
            <div className="stat__label">DONE</div>
            <div className="stat__value">{count('done')}</div>
            <div className="stat__note">ticked off</div>
          </div>
        </div>
      )}

      <div className="filter-row">
        <input className="control filter-row__search" placeholder="Find: description, outcome, who…" aria-label="Find a to-do"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <MultiSelect options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s], count: count(s) }))}
          selected={statuses} onChange={setStatuses} allLabel="Every item" label="Status" />
        <MultiSelect
          options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c], count: all.filter((t) => t.category === c).length }))}
          selected={categories} onChange={setCategories} allLabel="Every type" label="Type" />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ExportMenu onExport={exportRows} />
          {canKeep && <button className="btn btn--primary" onClick={() => setEditing('new')}>Add a to-do</button>}
        </div>
      </div>

      <div className="meta" style={{ marginBottom: 8 }}>
        {todos.isLoading ? 'Loading the list…' : `${sorted.length} of ${all.length} items`}
      </div>

      <div className="table-wrap only-wide">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="DONE" column="done" sort={sort} onSort={onSort} style={{ textAlign: 'center', width: 60 }} />
              <SortableHeader label="ENTERED" column="enteredOn" sort={sort} onSort={onSort} />
              <SortableHeader label="DESCRIPTION" column="description" sort={sort} onSort={onSort} />
              <SortableHeader label="TYPE" column="category" sort={sort} onSort={onSort} />
              <SortableHeader label="EXPECTED DONE" column="dueOn" sort={sort} onSort={onSort} />
              <SortableHeader label="DONE ON" column="doneOn" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {todos.isLoading && <EmptyRow colSpan={6}>Loading the list…</EmptyRow>}
            {todos.data && sorted.length === 0 && (
              <EmptyRow colSpan={6}>{all.length === 0 ? 'Nothing on the to-do list yet.' : 'No item matches these filters.'}</EmptyRow>
            )}
            {sorted.map((t) => (
              <tr key={t.toDoId} className={canKeep ? 'clickable' : undefined} onClick={() => canKeep && setEditing(t)}
                style={t.status === 'done' ? { opacity: 0.6 } : undefined}>
                <td style={{ textAlign: 'center' }}><Tick t={t} canTick={canKeep} busy={tick.isPending} onTick={onTick} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {formatDate(t.enteredOn)}
                  <div className="meta">{t.createdByName}</div>
                </td>
                <td style={{ maxWidth: 420 }}>
                  <div style={{ color: 'var(--text)', whiteSpace: 'pre-line', textDecoration: t.status === 'done' ? 'line-through' : undefined }}>
                    {t.description}
                  </div>
                  {t.resolution && <div className="meta" style={{ whiteSpace: 'pre-line' }}>Outcome: {t.resolution}</div>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{CATEGORY_LABEL[t.category]}</td>
                <td style={{ whiteSpace: 'nowrap' }}><DueText t={t} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {t.doneOn ? formatDate(t.doneOn) : '—'}
                  {t.doneByName && <div className="meta">{t.doneByName}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row-cards only-narrow">
        {sorted.map((t) => (
          <div key={t.toDoId} className="row-card" onClick={() => canKeep && setEditing(t)}
            style={t.status === 'done' ? { opacity: 0.6 } : undefined}>
            <div className="row-card__top" style={{ alignItems: 'flex-start', gap: 10 }}>
              <span style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Tick t={t} canTick={canKeep} busy={tick.isPending} onTick={onTick} />
                <span className="strong" style={{ textDecoration: t.status === 'done' ? 'line-through' : undefined }}>{t.description}</span>
              </span>
            </div>
            <div className="row-card__line">{CATEGORY_LABEL[t.category]} · entered {formatDate(t.enteredOn)}</div>
            <div className="row-card__line"><DueText t={t} long /></div>
            {t.resolution && <div className="row-card__line">Outcome: {t.resolution}</div>}
          </div>
        ))}
      </div>

      {editing && <ToDoForm todo={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** The done box. Ticking records today and who ticked it; unticking reopens the item. */
function Tick({ t, canTick, busy, onTick }: {
  t: ToDo; canTick: boolean; busy: boolean; onTick: (t: ToDo, done: boolean) => void;
}) {
  const label = t.status === 'done' ? 'Done — untick to reopen' : 'Tick when done';
  return (
    <input type="checkbox" checked={t.status === 'done'} disabled={!canTick || busy} aria-label={label} title={label}
      style={{ width: 18, height: 18, cursor: canTick ? 'pointer' : undefined }}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onTick(t, e.target.checked)} />
  );
}

/** The expected date, red once it has passed on something still open. */
function DueText({ t, long }: { t: ToDo; long?: boolean }) {
  if (t.status === 'done') {
    return <span style={{ color: 'var(--muted)' }}>{long ? `Done ${formatDate(t.doneOn!)}` : t.dueOn ? formatDate(t.dueOn) : '—'}</span>;
  }
  if (!t.dueOn) return <span className="meta">{long ? 'No date expected' : '—'}</span>;
  return (
    <span style={{ color: t.status === 'overdue' ? 'var(--red)' : 'var(--text)', fontWeight: t.status === 'overdue' ? 700 : undefined }}>
      {long ? 'Expected ' : ''}{formatDate(t.dueOn)}{t.status === 'overdue' ? ' · overdue' : ''}
    </span>
  );
}

/** Adding a to-do, or changing, ticking off or removing one. */
function ToDoForm({ todo, onClose }: { todo?: ToDo; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [enteredOn, setEnteredOn] = useState(todo?.enteredOn ?? today());
  const [description, setDescription] = useState(todo?.description ?? '');
  const [dueOn, setDueOn] = useState(todo?.dueOn ?? '');
  const [category, setCategory] = useState<ToDoCategory>(todo?.category ?? 'general');
  const [done, setDone] = useState(todo?.status === 'done');
  const [doneOn, setDoneOn] = useState(todo?.doneOn ?? today());
  const [resolution, setResolution] = useState(todo?.resolution ?? '');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['todos'] });

  const save = useMutation({
    mutationFn: async () => {
      const body: ToDoWrite = {
        enteredOn: enteredOn || null,
        description: description.trim(),
        dueOn: dueOn || null,
        category,
        done,
        doneOn: done ? doneOn || null : null,
        resolution: resolution.trim() || null,
        rowVersionNo: todo?.rowVersionNo,
      };
      return todo ? api.put(`/api/todos/${todo.toDoId}`, body) : api.post('/api/todos', body);
    },
    onSuccess: () => { refresh(); onClose(); },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/todos/${todo!.toDoId}`),
    onSuccess: () => { refresh(); onClose(); },
    onError: (e: Error) => setError(e.message),
  });

  const ready = description.trim() !== '' && enteredOn !== '' && !(done && !doneOn);

  return (
    <SlideOver
      title={todo ? 'To-do' : 'Add a to-do'}
      onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          {todo && (
            <button className="btn btn--sm" style={{ color: 'var(--red)' }} disabled={remove.isPending}
              onClick={() => { if (window.confirm('Remove this to-do? Use this for one added by mistake — a finished one is ticked instead.')) remove.mutate(); }}>
              Remove
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn--sm" onClick={onClose}>Cancel</button>
            <button className="btn btn--sm btn--primary" disabled={!ready || save.isPending}
              onClick={() => { setError(null); save.mutate(); }}>
              {save.isPending ? 'Saving…' : todo ? 'Save' : 'Add to-do'}
            </button>
          </div>
        </div>
      )}
    >
      {conflict && <ConflictBanner message={conflict} onReload={() => { refresh(); onClose(); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="td-desc">DESCRIPTION</label>
        <textarea id="td-desc" className="control" rows={3} style={{ width: '100%' }} value={description} autoFocus={!todo}
          placeholder="What needs doing" onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="td-type">TYPE</label>
        <Select id="td-type" className="control" value={category} onChange={(e) => setCategory(e.target.value as ToDoCategory)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </Select>
        <div className="meta">For a particular unit, put its name in the description.</div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <label className="field-label" htmlFor="td-entered">DATE ENTERED</label>
          <input id="td-entered" type="date" className="control" max={today()} value={enteredOn}
            onChange={(e) => setEnteredOn(e.target.value)} />
        </div>
        <div>
          <label className="field-label" htmlFor="td-due">EXPECTED DONE</label>
          <input id="td-due" type="date" className="control" min={enteredOn || undefined} value={dueOn}
            onChange={(e) => setDueOn(e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" style={{ width: 18, height: 18 }} checked={done} onChange={(e) => setDone(e.target.checked)} />
          Done
        </label>
        {done && (
          <div style={{ marginTop: 8 }}>
            <label className="field-label" htmlFor="td-done">DONE ON</label>
            <input id="td-done" type="date" className="control" min={enteredOn || undefined} max={today()} value={doneOn}
              onChange={(e) => setDoneOn(e.target.value)} />
            {todo?.doneByName && <span className="meta" style={{ marginLeft: 10 }}>ticked by {todo.doneByName}</span>}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="td-res">OUTCOME</label>
        <textarea id="td-res" className="control" rows={2} style={{ width: '100%' }} value={resolution}
          placeholder="What was done, or what came of it" onChange={(e) => setResolution(e.target.value)} />
      </div>

      {todo && (
        <div className="meta">
          Entered by {todo.createdByName}{todo.updatedAtUtc ? ` · last changed ${formatDate(todo.updatedAtUtc)}` : ''}
        </div>
      )}
    </SlideOver>
  );
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
